/**
 * Phone utilities - Canonical E.164 format contract
 *
 * Canonical format: E.164 with leading + (e.g. +5491122334455)
 * - Persistence (DB): E.164
 * - API payloads: E.164
 * - WhatsApp wa.me: digits only, no + (convert at usage boundary)
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

const DEFAULT_COUNTRY = 'AR';
const ALLOW_POSSIBLE_BUT_INVALID = process.env.REACT_APP_PHONE_ALLOW_POSSIBLE_WARNING === 'true';

/**
 * Validates a phone value. Accepts E.164, national format, or digits.
 * @param {string} phoneValue - Raw phone input
 * @param {string} defaultCountry - Country code for parsing (default AR)
 * @returns {{ valid: boolean, e164: string|null, error: string|null }}
 */
export function validatePhoneValue(phoneValue, defaultCountry = DEFAULT_COUNTRY) {
  if (!phoneValue || typeof phoneValue !== 'string') {
    return { valid: false, e164: null, error: 'El teléfono celular es requerido' };
  }

  const trimmed = phoneValue.trim();
  if (!trimmed) {
    return { valid: false, e164: null, error: 'El teléfono celular es requerido' };
  }

  try {
    const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
    if (!parsed) {
      return { valid: false, e164: null, error: 'No pudimos interpretar el número. Revisa país y formato.' };
    }

    const isValid = parsed.isValid();
    const isPossible = parsed.isPossible();

    if (isValid) {
      return { valid: true, e164: parsed.number, error: null };
    }
    if (ALLOW_POSSIBLE_BUT_INVALID && isPossible) {
      return { valid: true, e164: parsed.number, error: null };
    }

    return {
      valid: false,
      e164: parsed.number,
      error: 'El número no es válido para el país seleccionado'
    };
  } catch (error) {
    return {
      valid: false,
      e164: null,
      error: 'No pudimos interpretar el número. Revisa país y formato.'
    };
  }
}

/**
 * Parses any phone input to canonical E.164 format.
 * @param {string} phoneValue - Raw phone input
 * @param {string} defaultCountry - Country code for parsing (default AR)
 * @returns {string|null} E.164 format or null if unparseable
 */
export function parseToE164(phoneValue, defaultCountry = DEFAULT_COUNTRY) {
  if (!phoneValue || typeof phoneValue !== 'string') return null;
  const trimmed = phoneValue.trim();
  if (!trimmed) return null;

  try {
    const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
    return parsed?.number ?? null;
  } catch (error) {
    return null;
  }
}

/**
 * Converts E.164 to format required by wa.me (digits only, no +).
 * Use at the boundary when building WhatsApp links.
 * @param {string} e164 - Phone in E.164 format (e.g. +5491122334455)
 * @returns {string} Digits only for wa.me (e.g. 5491122334455)
 */
export function formatForWhatsApp(e164) {
  if (!e164 || typeof e164 !== 'string') return '';
  return e164.replace(/\D/g, '');
}
